"use client";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import { Eye, EyeOff, UserCheck, UserPlus } from "lucide-react";
import { useState } from "react";
import { useLogin } from "@/api/hook/useAuth";
import { LoginSchema } from "@/api/validation/auth-validation";
import Header from "@/components/layout/Header";
import { useGoogleAuth } from "@/api/hook/useGoogleAuth";
import { useRouter } from "next/navigation";

// ── Account Check Modal ───────────────────────────────────────────────────────

function AccountCheckModal({
  onHasAccount,
  onNoAccount,
}: {
  onHasAccount: () => void;
  onNoAccount: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-[#FFF] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-[#1C1B4D] px-6 py-6 text-center">
          <h2 className="text-[#FFF] text-base font-bold mb-1">
            Welcome to La Plage Meta Verse
          </h2>
          <p className="text-[#FFF] text-lg">Do you have an account with us?</p>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <button
            onClick={onHasAccount}
            className="w-full flex items-center gap-4 bg-primary hover:bg-primary/90 text-[#FFF] rounded-xl px-5 py-4 transition-all duration-200"
          >
            <div className="w-10 h-10 rounded-full bg-[#FFF]/20 flex items-center justify-center shrink-0">
              <UserCheck className="w-5 h-5 text-[#FFF]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-base">Yes, I have an account</p>
              <p className="text-[#FFF]/70 text-sm mt-0.5">
                Sign in to your account
              </p>
            </div>
          </button>

          <button
            onClick={onNoAccount}
            className="w-full flex items-center gap-4 bg-[#FFF] border-2 border-primary hover:bg-primary/5 text-primary rounded-xl px-5 py-4 transition-all duration-200"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-bold text-base">No, I'm new here</p>
              <p className="text-primary/60 text-sm mt-0.5">
                Create a free account
              </p>
            </div>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 pb-5">
          Registration is completely free
        </p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const LoginPage = () => {
  const router = useRouter();
  const loginMutation = useLogin();
  const { signInWithGoogle, isLoading: isGoogleLoading } = useGoogleAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(true);

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle();
      router.push("/dashboard");
    } catch (error) {
      console.error("Google sign up error:", error);
    }
  };

  return (
    <>
      <Header />

      {showAccountModal && (
        <AccountCheckModal
          onHasAccount={() => setShowAccountModal(false)}
          onNoAccount={() => router.push("/sign-up")}
        />
      )}

      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section */}
        <div className="hidden md:flex w-full lg:w-2/5 bg-[#1C1B4D] flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-12">
          <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
            Start learning with
          </h1>
          <h1
            className="font-bold text-2xl sm:text-4xl md:text-5xl lg:text-6xl mt-2 text-center lg:text-left"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            La Plage Meta Verse
          </h1>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-3/5 flex justify-center items-center px-6 sm:px-10 py-12">
          <div className="w-full max-w-md">
            <h3 className="font-bold text-primary text-3xl sm:text-4xl text-center mb-8">
              Welcome back!
            </h3>

            <Formik
              initialValues={{ email: "", password: "" }}
              validationSchema={LoginSchema}
              onSubmit={(values, { setSubmitting }) => {
                loginMutation.mutate(values);
                setSubmitting(false);
              }}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="flex flex-col gap-6">
                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Email Address
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className={`rounded-md py-3 border ${
                        errors.email && touched.email
                          ? "border-[#d30a0a]"
                          : "border-[#111]"
                      }`}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-[#d30a0a] text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Password
                    </label>
                    <div className="relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className={`rounded-md py-3 p border w-full ${
                          errors.password && touched.password
                            ? "border-[#d30a0a]"
                            : "border-[#111]"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-[#d30a0a] text-sm"
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <Link
                      href="/forgot-password"
                      className="font-bold text-sm md:text-lg sm:text-xl text-primary hover:text-primary/80"
                    >
                      Forgot Password
                    </Link>
                    <Link
                      href="/forgot-email"
                      className="font-bold text-sm md:text-lg sm:text-xl text-primary hover:text-primary/80"
                    >
                      Forgot Email
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || loginMutation.isPending}
                    className={`bg-primary rounded-md text-[#FFF] text-lg sm:text-xl font-bold py-3 hover:bg-primary/90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${
                      (isSubmitting || loginMutation.isPending) &&
                      "cursor-wait opacity-50"
                    }`}
                  >
                    {isSubmitting || loginMutation.isPending
                      ? "Logging in...."
                      : "Log In"}
                  </button>

                  <p className="font-medium text-lg sm:text-xl text-center">
                    or
                  </p>

                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      onClick={handleGoogleSignUp}
                      disabled={isGoogleLoading}
                      className="border rounded-md flex items-center py-3 px-4 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FcGoogle className="w-5 h-5" />
                      <p className="text-center flex-1">
                        {isGoogleLoading
                          ? "Connecting..."
                          : "Continue with Google"}
                      </p>
                    </button>
                  </div>

                  <p className="text-lg sm:text-xl text-center">
                    Don't have an account?{" "}
                    <Link
                      href="/sign-up"
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      Sign up now!
                    </Link>
                  </p>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
